import { Accordion, AccordionDetails, AccordionSummary, Paper, Link } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { isStudyTypeLabel } from "@shared/scorecard";
import type { EvidenceSource, EvidenceVerdict } from "../../../../types/claims";
import { QuoteBlock } from "../QuoteBlock";
import { Caption } from "../../../primitives/data-display/Caption";
import { Meta } from "../../../primitives/data-display/Meta";
import { VerdictDot } from "../VerdictDot";
import { StatedStudyTypeHint } from "../StatedStudyTypeHint";
import { compareStatedStudyType } from "../../../../lib/stated_study_type";
import {
  QUALITY_RATING_LABEL_KEYS,
  qualityRatingBucket,
} from "../../../../lib/qualityRatingBucket";

const SOURCE_VERDICT_COLORS: Record<EvidenceVerdict, string> = {
  SUPPORTS: "#77A290",
  CONTRADICTS: "#BA7517",
  INCONCLUSIVE: "#888780",
};

const SPEC_KEYS: { key: string; label: string }[] = [
  { key: "study_type", label: "Study type" },
  { key: "sample_size", label: "Sample size" },
  { key: "sampling_method", label: "Sampling method" },
  { key: "population_diversity", label: "Population diversity" },
  { key: "peer_review", label: "Publication" },
  { key: "funding", label: "Funding" },
];

interface SourceCardProps {
  evidence: EvidenceSource;
  anchorId?: string;
  /** PubMed's publication types for this source, when known; used to flag a stated study type
   *  that differs from the classified one. */
  publicationTypes?: readonly string[];
}

const OverflowPaper = styled(Paper)({ overflow: "hidden" });

const HeaderRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingLeft: theme.spacing(2.25),
  paddingRight: theme.spacing(2.25),
  paddingTop: theme.spacing(1.75),
  paddingBottom: theme.spacing(1),
}));

const VerdictRow = styled("div")({ display: "flex", alignItems: "center", gap: 6 });

const TitleLink = styled(Link)(({ theme }) => ({
  display: "block",
  fontWeight: 500,
  paddingLeft: theme.spacing(2.25),
  paddingRight: theme.spacing(2.25),
}));

const TitleCaption = styled(Caption)(({ theme }) => ({
  fontWeight: 500,
  paddingLeft: theme.spacing(2.25),
  paddingRight: theme.spacing(2.25),
}));

const AuthorsMeta = styled(Meta)(({ theme }) => ({
  display: "block",
  paddingLeft: theme.spacing(2.25),
  paddingRight: theme.spacing(2.25),
  marginTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(1.75),
  overflowWrap: "break-word",
}));

const QualityRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  paddingLeft: theme.spacing(2.25),
  paddingRight: theme.spacing(2.25),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderTop: `0.5px solid ${theme.palette.divider}`,
}));

const QualityLabelCaption = styled(Caption)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const QualityValueCaption = styled(Caption)({ fontWeight: 500 });

const SpecRow = styled("div")(({ theme }) => ({
  display: "flex",
  paddingLeft: theme.spacing(2.25),
  paddingRight: theme.spacing(2.25),
  paddingTop: "7px",
  paddingBottom: "7px",
  borderTop: `0.5px solid ${theme.palette.divider}`,
}));

const SpecKey = styled(Caption)({ flex: "0 0 160px" });

const SpecValue = styled(Caption)({ flex: 1 });

const TransparentAccordion = styled(Accordion)({
  "&::before": { display: "none" },
  backgroundColor: "transparent",
});

function SourceSpec({ label, value, hint }: { label: string; value?: string; hint?: ReactNode }) {
  return (
    <SpecRow>
      <SpecKey color="textSecondary">{label}</SpecKey>
      <SpecValue color="textPrimary">
        {value}
        {hint}
      </SpecValue>
    </SpecRow>
  );
}

export function SourceCard({ evidence, anchorId, publicationTypes }: SourceCardProps) {
  const { t } = useTranslation();

  const verdictLabels: Record<EvidenceVerdict, string> = {
    SUPPORTS: t("sourceCard.supports"),
    CONTRADICTS: t("sourceCard.contradicts"),
    INCONCLUSIVE: t("sourceCard.inconclusive"),
  };

  const verdictColor = SOURCE_VERDICT_COLORS[evidence.verdict];

  const year = evidence.published_date ? evidence.published_date.slice(0, 4) : null;
  const qualityBucket = qualityRatingBucket(evidence.cross_design_quality);
  const qualityLabel = t(QUALITY_RATING_LABEL_KEYS[qualityBucket]);

  const annotatedStudyType = evidence.scorecard_labels?.study_type?.[0];
  const statedStudyType =
    publicationTypes && annotatedStudyType && isStudyTypeLabel(annotatedStudyType)
      ? compareStatedStudyType(publicationTypes, annotatedStudyType)
      : null;
  const studyTypeHint =
    annotatedStudyType && statedStudyType?.isMismatch && statedStudyType.statedPublicationType ? (
      <StatedStudyTypeHint
        statedPublicationType={statedStudyType.statedPublicationType}
        annotatedStudyType={annotatedStudyType}
      />
    ) : undefined;

  const specRows = SPEC_KEYS.flatMap(({ key, label }) => {
    const values = evidence.scorecard_labels?.[key];
    if (!values?.length) return [];
    return [
      {
        key,
        label,
        value: values.join(", "),
        hint: key === "study_type" ? studyTypeHint : undefined,
      },
    ];
  });

  const hasEvidence = evidence.quotes.length > 0;

  return (
    <OverflowPaper id={anchorId}>
      {/* Header row: verdict tag */}
      <HeaderRow>
        <VerdictRow>
          <VerdictDot $color={verdictColor} />
          <Caption>{verdictLabels[evidence.verdict]}</Caption>
        </VerdictRow>
      </HeaderRow>

      {/* Title */}
      {evidence.url ? (
        <TitleLink href={evidence.url} target="_blank" rel="noopener noreferrer">
          <Caption color="primary">{evidence.title || t("sourceCard.untitled")}</Caption>
        </TitleLink>
      ) : (
        <TitleCaption>{evidence.title || t("sourceCard.untitled")}</TitleCaption>
      )}

      {/* Authors */}
      <AuthorsMeta>{evidence.authors}</AuthorsMeta>

      {/* Quality rating */}
      <QualityRow>
        <QualityLabelCaption>{t("sourceCard.methodologyQuality")}</QualityLabelCaption>
        <QualityValueCaption>{qualityLabel}</QualityValueCaption>
      </QualityRow>

      {/* Spec rows */}
      {specRows.map(({ key, label, value, hint }) => (
        <SourceSpec key={key} label={label} value={value} hint={hint} />
      ))}
      <SourceSpec label="Date" value={year?.toString()} />

      {/* Supporting evidence accordion */}
      {hasEvidence && (
        <TransparentAccordion disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Caption>{t("sourceCard.supportingEvidence")}</Caption>
          </AccordionSummary>
          <AccordionDetails style={{ paddingTop: 0 }}>
            {evidence.quotes.map((q, i) => (
              <QuoteBlock key={i} quote={q} />
            ))}
          </AccordionDetails>
        </TransparentAccordion>
      )}
    </OverflowPaper>
  );
}
